import React, { useState } from 'react';
import { Building2, Save, Crown, Trash2, AlertTriangle, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Loading } from '../../components/ui/Loading';
import { useOrganization, useUpdateOrganization, useDataDeletionPreview, useDeleteAllData } from '../../hooks/useOrganization';
import { useAuthStore } from '../../store/authStore';
import { formatDate } from '../../lib/utils';
import toast from 'react-hot-toast';

const Organization: React.FC = () => {
    const { data: organization, isLoading } = useOrganization();
    const updateMutation = useUpdateOrganization();
    const { refetch: fetchPreview, data: deletionPreview, isFetching: isLoadingPreview } = useDataDeletionPreview();
    const deleteAllMutation = useDeleteAllData();
    const user = useAuthStore((state) => state.user);

    const [name, setName] = useState('');
    const [emailAlerts, setEmailAlerts] = useState(true);
    const [slackWebhook, setSlackWebhook] = useState('');

    // Data deletion modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [password, setPassword] = useState('');

    React.useEffect(() => {
        if (organization) {
            setName(organization.name);
            setEmailAlerts(organization.settings?.notifications?.email_alerts ?? true);
            setSlackWebhook(organization.settings?.notifications?.slack_webhook || '');
        }
    }, [organization]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await updateMutation.mutateAsync({
            name,
            settings: {
                notifications: {
                    email_alerts: emailAlerts,
                    slack_webhook: slackWebhook || undefined,
                },
            },
        });
    };

    const handleOpenDeleteModal = async () => {
        // Check if user is owner
        if (user?.role !== 'owner') {
            toast.error('Only organization owner can delete data');
            return;
        }

        setShowDeleteModal(true);
        await fetchPreview();
    };

    const handleDeleteAllData = async (e: React.FormEvent) => {
        e.preventDefault();

        if (confirmText !== 'DELETE_ALL_MY_DATA') {
            toast.error('Please type the confirmation text exactly');
            return;
        }

        if (!password) {
            toast.error('Password is required');
            return;
        }

        try {
            await deleteAllMutation.mutateAsync({
                confirm: confirmText,
                password: password,
            });

            setShowDeleteModal(false);
            setConfirmText('');
            setPassword('');
        } catch (error: any) {
            // Handle specific validation errors
            const errorCode = error.response?.data?.error?.code;
            const errorDetails = error.response?.data?.error?.details;
            const errorMessage = error.response?.data?.error?.message;

            if (errorCode === 'VALIDATION_ERROR') {
                if (errorDetails?.password) {
                    toast.error(errorDetails.password[0] || 'Incorrect password');
                } else if (errorDetails?.confirm) {
                    toast.error(errorDetails.confirm[0] || 'Invalid confirmation text');
                } else {
                    toast.error(errorMessage || 'Validation failed');
                }
            } else if (errorCode === 'UNAUTHORIZED') {
                toast.error('Only organization owner can delete data');
            } else {
                toast.error(errorMessage || 'Failed to delete data. Please try again.');
            }
        }
    };

    if (isLoading) {
        return <Loading fullScreen message="Loading organization settings..." />;
    }

    const getPlanColor = (plan: string) => {
        switch (plan.toLowerCase()) {
            case 'free':
                return 'text-gray-700 bg-gray-100';
            case 'basic':
                return 'text-blue-700 bg-blue-100';
            case 'standard':
                return 'text-green-700 bg-green-100';
            case 'pro':
                return 'text-purple-700 bg-purple-100';
            default:
                return 'text-gray-700 bg-gray-100';
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Organization Settings</h1>
                <p className="text-gray-600 mt-1">Manage your organization details and preferences</p>
            </div>

            {/* Organization Info */}
            <Card>
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Organization Information</h2>
                        <div className="flex items-center gap-2">
                            <Badge className={getPlanColor(
                                organization?.plan?.code || (organization?.plan as any) || 'free'
                            )}>
                                {organization?.plan?.name || organization?.plan_label || 'Unknown'}
                            </Badge>
                        </div>
                    </div>
                    <Building2 className="w-12 h-12 text-gray-400" />
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Basic Info */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Organization Name
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            required
                        />
                    </div>

                    {/* API Requests (Read-only) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            API Requests
                        </label>
                        <div className="flex items-center gap-3">
                            <input
                                type="text"
                                value="Unlimited"
                                disabled
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                            />
                            <Badge className="bg-green-100 text-green-700">
                                ✓ No Limits
                            </Badge>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            All plans now have unlimited API requests. Rate limiting has been removed.
                        </p>
                    </div>

                    {/* Notifications */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-gray-900">Notification Settings</h3>

                        <div className="flex items-center justify-between">
                            <div>
                                <label className="text-sm font-medium text-gray-700">Email Alerts</label>
                                <p className="text-xs text-gray-500">Receive email notifications for critical risks</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={emailAlerts}
                                    onChange={(e) => setEmailAlerts(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                            </label>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Slack Webhook URL (Optional)
                            </label>
                            <input
                                type="url"
                                value={slackWebhook}
                                onChange={(e) => setSlackWebhook(e.target.value)}
                                placeholder="https://hooks.slack.com/services/..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Get notified about critical events in your Slack workspace
                            </p>
                        </div>
                    </div>

                    {/* Submit */}
                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                        <button
                            type="submit"
                            disabled={updateMutation.isPending}
                            className="btn-primary flex items-center gap-2 disabled:opacity-50"
                        >
                            <Save className="w-4 h-4" />
                            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </Card>

            {/* Plan Information (Read-only) */}
            <Card>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Current Plan</h2>
                    <Link
                        to="/pricing"
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
                    >
                        <Crown className="w-4 h-4" />
                        View Plans
                    </Link>
                </div>
                <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">Current Plan</span>
                        <Badge className={getPlanColor(
                            organization?.plan?.code || (organization?.plan as any) || 'free'
                        )}>
                            {organization?.plan?.name || organization?.plan_label || 'Unknown'}
                        </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">API Requests</span>
                        <Badge className="bg-green-100 text-green-700">
                            Unlimited
                        </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">Team Members</span>
                        <span className="text-sm font-semibold text-gray-900">
                            {organization?.limits?.team_members || organization?.team_member_limit || 'Unlimited'}
                        </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">Data Retention</span>
                        <span className="text-sm font-semibold text-gray-900">
                            {organization?.retention_days} days
                        </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">Member Since</span>
                        <span className="text-sm font-semibold text-gray-900">
                            {organization?.created_at && formatDate(organization.created_at)}
                        </span>
                    </div>
                </div>

                {/* DEPRECATED: Trial countdown - removed for free plan model */}
            </Card>

            {/* Danger Zone - Data Deletion */}
            {user?.role === 'owner' && (
                <Card>
                    <div className="flex items-start justify-between mb-6">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="w-5 h-5 text-red-600" />
                                <h2 className="text-lg font-semibold text-red-900">Danger Zone</h2>
                            </div>
                            <p className="text-sm text-gray-600">Permanently delete all your organization data</p>
                        </div>
                        <Trash2 className="w-12 h-12 text-red-400" />
                    </div>

                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                        <p className="text-sm text-red-800 font-medium mb-2">
                            ⚠️ <strong>WARNING:</strong> This action is IRREVERSIBLE!
                        </p>
                        <ul className="text-xs text-red-700 space-y-1 ml-4 list-disc">
                            <li>All payment history will be permanently deleted</li>
                            <li>Reports and analytics will be reset</li>
                            <li>Detected patterns and intelligence data will be cleared</li>
                            <li>Your monthly usage count will NOT be reset</li>
                        </ul>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg mb-4">
                        <p className="text-sm text-gray-700 font-medium mb-2">✅ What will be preserved:</p>
                        <ul className="text-xs text-gray-600 space-y-1 ml-4 list-disc">
                            <li>Your account and team members</li>
                            <li>API keys and provider configurations</li>
                            <li>Organization settings</li>
                            <li>Audit logs (for compliance)</li>
                        </ul>
                    </div>

                    <button
                        onClick={handleOpenDeleteModal}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                    >
                        <Trash2 className="w-4 h-4" />
                        Delete All Data
                    </button>
                </Card>
            )}

            {/* Data Deletion Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center gap-3 mb-6">
                            <AlertTriangle className="w-8 h-8 text-red-600" />
                            <h2 className="text-2xl font-bold text-red-900">Delete All Data</h2>
                        </div>

                        {isLoadingPreview ? (
                            <div className="flex items-center justify-center py-12">
                                <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
                            </div>
                        ) : deletionPreview ? (
                            <form onSubmit={handleDeleteAllData} className="space-y-6">
                                {/* Preview Stats */}
                                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm font-semibold text-red-900 mb-3">
                                        📊 The following data will be PERMANENTLY DELETED:
                                    </p>
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div className="flex justify-between">
                                            <span className="text-gray-700">📊 Payment Intents:</span>
                                            <span className="font-semibold">{deletionPreview.records_to_delete.payment_intents.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-700">📝 Payment Events:</span>
                                            <span className="font-semibold">{deletionPreview.records_to_delete.payment_events.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-700">🔄 Payment States:</span>
                                            <span className="font-semibold">{deletionPreview.records_to_delete.payment_states.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-700">💰 Revenue Records:</span>
                                            <span className="font-semibold">{deletionPreview.records_to_delete.revenue_records.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-700">🔔 Webhook Events:</span>
                                            <span className="font-semibold">{deletionPreview.records_to_delete.webhook_events.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-700">⚠️ Risk Flags:</span>
                                            <span className="font-semibold">{deletionPreview.records_to_delete.risk_flags.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-700">🔍 Patterns:</span>
                                            <span className="font-semibold">{deletionPreview.records_to_delete.detected_patterns.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-700">📈 Intelligence:</span>
                                            <span className="font-semibold">{deletionPreview.records_to_delete.payment_features.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-700">📄 Reports:</span>
                                            <span className="font-semibold">{deletionPreview.records_to_delete.reports.toLocaleString()}</span>
                                        </div>
                                    </div>
                                    <div className="mt-3 pt-3 border-t border-red-300">
                                        <div className="flex justify-between text-sm font-bold text-red-900">
                                            <span>📦 TOTAL:</span>
                                            <span>{deletionPreview.total_records.toLocaleString()} records</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Usage Info */}
                                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <p className="text-sm font-semibold text-yellow-900 mb-2">
                                        ⚠️ IMPORTANT: Your monthly usage will NOT reset!
                                    </p>
                                    <p className="text-xs text-yellow-800">
                                        Current usage: <strong>{deletionPreview.usage_info.current_usage.toLocaleString()}</strong> / {deletionPreview.usage_info.usage_limit.toLocaleString()} ({deletionPreview.usage_info.usage_percentage}%) will remain.
                                    </p>
                                    <p className="text-xs text-yellow-700 mt-1">
                                        {deletionPreview.usage_info.note}
                                    </p>
                                </div>

                                {/* Warnings */}
                                <div className="p-4 bg-red-50 border-2 border-red-300 rounded-lg">
                                    <p className="text-sm font-semibold text-red-900 mb-2">🚨 Final Warnings:</p>
                                    <ul className="text-xs text-red-800 space-y-1">
                                        {deletionPreview.warnings.map((warning, idx) => (
                                            <li key={idx}>• {warning}</li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Confirmation Input */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-900 mb-2">
                                        To confirm, type: <code className="bg-gray-100 px-2 py-1 rounded text-red-600 font-mono">DELETE_ALL_MY_DATA</code>
                                    </label>
                                    <input
                                        type="text"
                                        value={confirmText}
                                        onChange={(e) => setConfirmText(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 font-mono"
                                        placeholder="DELETE_ALL_MY_DATA"
                                        required
                                    />
                                </div>

                                {/* Password Input */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-900 mb-2">
                                        Enter your password to confirm:
                                    </label>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                                        placeholder="Your password"
                                        required
                                    />
                                </div>

                                {/* Actions */}
                                <div className="flex gap-3 pt-4 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowDeleteModal(false);
                                            setConfirmText('');
                                            setPassword('');
                                        }}
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={deleteAllMutation.isPending || confirmText !== 'DELETE_ALL_MY_DATA'}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        {deleteAllMutation.isPending ? 'Deleting...' : 'Delete All Data'}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                Failed to load deletion preview
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Organization;
